import {useMemo} from 'react'
/**
 * useHasInnerText
 *
 * React hook to check if an HTML string contains any visible text content.
 * Returns true if there is non-whitespace text inside the tags, false otherwise.
 *
 * Useful for determining if a string like '<p><br></p>' or '<div></div>' is actually empty,
 * or if it contains real content. This is helpful for conditional rendering of HTML blocks
 * that may be populated by a CMS or WYSIWYG editor.
 *
 * @param html - The HTML string to check for visible text content.
 * @returns boolean - True if there is visible text, false if only empty tags/whitespace.
 *
 * Example:
 *   const hasContent = useHasInnerText(subText)
 *   if (hasContent) { ...render... }
 */

export function useHasInnerText(html: string): boolean {
    return useMemo(() => {
        if (!html || typeof html !== 'string') return false
        if (typeof document === 'undefined') return false
        // Create a temporary DOM element to parse the HTML
        const temp = document.createElement('div')
        temp.innerHTML = html
        // Get the innerText, which strips tags and whitespace
        const text = temp.innerText.replace(/\s+/g, '')
        return text.length > 0
    }, [html])
}
